import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

// EP-73 — the daily AI-coach suggestion. The server owns selection; this
// hook just reads the cached row (204 → null) and exposes the action
// mutations. Enabled is the client half of the dual gate + a once/day
// localStorage check so the bubble doesn't even fetch twice in a day.

export interface AssistantWalkthrough {
  title?: string
  steps?: string[]
  time_estimate_minutes?: number
  why_this_matters?: string
  source: 'llm' | 'static'
  feature_id?: string
  assets?: Array<{ kind: string; key: string }>
}

export interface AssistantSuggestion {
  id: number
  feature_id: string
  kind: 'resume' | 'discover'
  target_ref: string | null
  status: string
  walkthrough: AssistantWalkthrough
}

export const assistantKeys = {
  all: ['assistant'] as const,
  suggestion: () => [...assistantKeys.all, 'suggestion'] as const,
}

const LAST_SHOWN_PREFIX = 'aiCoach_lastShown_'

// DAT-1: the device's own local calendar day, not the UTC day
// `toISOString().slice(0, 10)` would give: "shown today" means today on the
// person's own clock. This hook has no account context to read a configured
// timezone from, so it uses the browser's local fields directly (never a
// UTC round trip), which is the right frame for a client-only dedup key.
function todayStamp(): string {
  const d = new Date()
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

export function alreadyShownToday(accountId: number): boolean {
  try {
    return localStorage.getItem(`${LAST_SHOWN_PREFIX}${accountId}`) === todayStamp()
  } catch {
    return false
  }
}

export function markShownToday(accountId: number): void {
  try {
    localStorage.setItem(`${LAST_SHOWN_PREFIX}${accountId}`, todayStamp())
  } catch {
    // private browsing / quota — non-fatal
  }
}

export function useAssistantSuggestion(enabled: boolean) {
  return useQuery({
    queryKey: assistantKeys.suggestion(),
    enabled,
    // The job recomputes daily; a 204 means "nothing yet" — refetch shortly.
    refetchInterval: (query) => (query.state.data == null ? 15_000 : false),
    queryFn: async (): Promise<AssistantSuggestion | null> => {
      const res = await api.get<{ data: AssistantSuggestion } | ''>('/api/assistant/suggestion', {
        validateStatus: (s) => s === 200 || s === 204,
      })
      if (res.status === 204 || !res.data) return null
      return (res.data as { data: AssistantSuggestion }).data
    },
  })
}

type Action = 'dismiss' | 'snooze' | 'complete'

export function useAssistantAction() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, action }: { id: number; action: Action }) => {
      await api.post(`/api/assistant/suggestions/${id}/${action}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: assistantKeys.suggestion() })
    },
  })
}
